import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatDate } from "@/lib/catalog";
import { Skeleton, EmptyState } from "@/components/Ui";
import { ArrowLeft } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [state, setState] = useState({ post: null, loading: true, notFound: false });

  useEffect(() => {
    setState({ post: null, loading: true, notFound: false });
    apiGet(`/blog/${slug}`)
      .then((post) => setState({ post, loading: false, notFound: false }))
      .catch((err) => setState({ post: null, loading: false, notFound: err.status === 404 }));
  }, [slug]);

  const { post, loading, notFound } = state;

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="blogpost-loading">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-6 h-10 w-full" />
        <Skeleton className="mt-3 h-10 w-2/3" />
        <Skeleton className="mt-8 h-64 w-full" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 sm:px-6" data-testid="blogpost-notfound">
        <EmptyState message={t("blog.notFound")} testid="blogpost-empty" />
        <div className="mt-6 text-center">
          <Link to="/blog" className="btn-outline" data-testid="blogpost-back">
            <ArrowLeft className="h-4 w-4" /> {t("blog.back")}
          </Link>
        </div>
      </div>
    );
  }

  const title = lang === "fr" ? post.title_fr : post.title_en;
  const content = lang === "fr" ? post.content_fr : post.content_en;
  const paragraphs = (content || "").split(/\n{2,}/).filter(Boolean);

  return (
    <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="blogpost-page">
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-dark" data-testid="blogpost-back">
        <ArrowLeft className="h-4 w-4" /> {t("blog.back")}
      </Link>
      {post.published_at && (
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-gold" data-testid="blogpost-date">
          {t("blog.published")} {formatDate(post.published_at, lang)}
        </p>
      )}
      <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-marine sm:text-5xl" data-testid="blogpost-title">
        {title}
      </h1>
      {post.cover_url && (
        <img src={post.cover_url} alt="" className="mt-8 w-full rounded-2xl object-cover" data-testid="blogpost-cover" />
      )}
      <div className="prose-mg mt-8" data-testid="blogpost-content">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </article>
  );
}
